import React, { useState } from "react";
import * as Icons from "lucide-react";
import { ArrowRight, Check } from "lucide-react";
import { DRYCLEAN_RATES, WASH_SERVICES, PREPAID_PLANS, BRAND } from "../mock";
import { useBooking } from "../App";
import Reveal from "./Reveal";

const TABS = [
  { id: "dryclean", label: "Dry Clean Rates" },
  { id: "wash", label: "Wash Rates" },
  { id: "plans", label: "Prepaid Plans" },
];

const RatesSection = () => {
  const [tab, setTab] = useState("dryclean");
  const { openBooking } = useBooking();

  return (
    <section id="rates" className="py-16 sm:py-20 bg-[#f7f9fc]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal className="text-center mb-10">
          <span className="text-orange-brand font-bold text-sm uppercase tracking-wide">As affordable as soap and water</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-navy font-head mt-2">Plans &amp; Prices for Laundry &amp; Dry Clean</h2>
        </Reveal>

        {/* Tabs */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-white rounded-full p-1.5 shadow-md border border-gray-100 flex-wrap justify-center">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-5 sm:px-7 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  tab === t.id ? "bg-navy text-white shadow" : "text-navy/70 hover:text-navy"
                }`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "dryclean" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DRYCLEAN_RATES.map((cat, i) => {
              const Icon = Icons[cat.icon] || Icons.Shirt;
              return (
                <Reveal key={cat.title} delay={i * 60}>
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow overflow-hidden h-full flex flex-col">
                    <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
                      <span className="w-11 h-11 rounded-xl bg-orange-brand/10 text-orange-brand flex items-center justify-center">
                        <Icon size={22} />
                      </span>
                      <h3 className="font-bold text-lg text-navy font-head">{cat.title}</h3>
                    </div>
                    <div className="px-6 py-2 flex-1">
                      {cat.items.map((it) => (
                        <div key={it.name} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0 text-sm">
                          <span className="text-navy/80">{it.name}</span>
                          <span className="font-semibold text-navy">
                            {it.mrp && <span className="text-gray-400 line-through mr-1.5 font-normal">₹{it.mrp}</span>}
                            ₹{it.price}<span className="text-[11px] text-gray-400 font-normal">{cat.unit}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="px-6 pb-5 pt-3">
                      <p className="text-[11px] text-gray-400 mb-3">*Prices shown with coupon {BRAND.coupon} applied. Inclusive of taxes.</p>
                      <button onClick={openBooking} className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-[#153a63] text-white font-semibold py-2.5 rounded-full transition-colors text-sm">
                        Book Now <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}

        {tab === "wash" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WASH_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 60}>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow p-6 h-full flex flex-col text-center">
                  <h3 className="font-bold text-lg text-navy font-head">{s.title}</h3>
                  <div className="my-3">
                    <span className="text-4xl font-extrabold text-orange-brand font-head">₹{s.price}</span>
                    <span className="text-navy/60 font-semibold">{s.unit}</span>
                  </div>
                  <p className="text-xs text-navy/50 -mt-1 mb-3">
                    Regular ₹{s.mrp}{s.unit} <span className="bg-blue-50 text-bluelink px-2 py-0.5 rounded-full text-[11px] font-semibold ml-1">{BRAND.coupon}</span>
                  </p>
                  <p className="text-sm text-navy/60 leading-relaxed flex-1">{s.desc}</p>
                  <ul className="text-left space-y-2 my-5">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-navy/80">
                        <Check size={16} className="text-green-500 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={openBooking} className="w-full bg-orange-brand hover:bg-[#e0850f] text-white font-semibold py-2.5 rounded-full transition-colors text-sm">
                    Book Now
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {tab === "plans" && (
          <>
            <p className="text-center text-navy/60 max-w-xl mx-auto mb-8 text-sm">
              Save more with a Laundryma prepaid plan — lower per-kg rates, dry-clean discounts, and free pickup &amp; delivery every time.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {PREPAID_PLANS.map((p, i) => (
                <Reveal key={p.title} delay={i * 60}>
                  <div className={`rounded-2xl p-6 h-full flex flex-col relative transition-shadow hover:shadow-xl ${
                    p.recommended ? "bg-navy text-white shadow-xl" : "bg-white border border-gray-100 shadow-sm"
                  }`}>
                    {p.recommended && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-brand text-white text-xs font-bold px-4 py-1 rounded-full">RECOMMENDED</span>
                    )}
                    <h3 className={`font-bold text-lg font-head ${p.recommended ? "text-white" : "text-navy"}`}>{p.title}</h3>
                    <div className="my-3">
                      <span className={`text-3xl font-extrabold font-head ${p.recommended ? "text-orange-brand" : "text-navy"}`}>₹{p.price}</span>
                      <span className={`text-sm font-semibold ${p.recommended ? "text-white/60" : "text-navy/50"}`}> / plan</span>
                    </div>
                    <p className={`text-xs mb-4 ${p.recommended ? "text-white/60" : "text-navy/50"}`}>{p.note}</p>
                    <ul className="space-y-2.5 flex-1 mb-5">
                      {p.features.map((f) => (
                        <li key={f} className={`flex items-start gap-2 text-sm ${p.recommended ? "text-white/85" : "text-navy/75"}`}>
                          <Check size={16} className={`shrink-0 mt-0.5 ${p.recommended ? "text-orange-brand" : "text-green-500"}`} /> {f}
                        </li>
                      ))}
                    </ul>
                    <button onClick={openBooking} className={`w-full font-semibold py-2.5 rounded-full transition-colors text-sm ${
                      p.recommended ? "bg-orange-brand hover:bg-[#e0850f] text-white" : "bg-navy hover:bg-[#153a63] text-white"
                    }`}>Choose Plan</button>
                  </div>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default RatesSection;
